/**
 * Workspace Settings — Expert Agents Management
 *
 * CRUD interface for managing Expert Agents in a workspace.
 * Plan-gated: requires ai_expert_bots feature (Team).
 *
 * UX: Create/Edit via Modal dialog with folder/doc tree picker.
 */

import { Button, Checkbox } from '@affine/component';
import { SettingWrapper } from '@affine/component/setting-components';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { EmbeddingService } from '@affine/core/modules/workspace-indexer-embedding';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  SettingsActionsSection,
  SettingsConfirmButton,
  SettingsContentSection,
  SettingsContentText,
  SettingsNotice,
} from '../shared/settings-content';
import {
  SettingsEntityList,
  SettingsExpandableItem,
  SettingsMetaItem,
} from '../shared/settings-entity';
import { SettingsEntityFormDialog } from '../shared/settings-form-dialog';
import { useSettingsEntityList } from '../shared/use-settings-entity-list';
import { requestWorkspaceSettingGraphQL } from '../shared/workspace-settings-api';

// =============================================================================
// Types
// =============================================================================

interface WorkspaceBot {
  id: string;
  name: string;
  description: string;
  systemPrompt: string;
  accessDocIds: string[];
  intersectionMode: boolean;
  isActive: boolean;
  createdAt: string;
}

interface BotFormData {
  name: string;
  description: string;
  systemPrompt: string;
  accessDocIds: string[];
  intersectionMode: boolean;
}

interface FolderInfo {
  id: string;
  name: string;
  parentId: string | null;
  isWorkspaceRoot: boolean;
  docIds: string[];
}

interface DocInfo {
  id: string;
  title: string;
}

// =============================================================================
// Inline styles — only for domain-specific widgets (FolderDocPicker, form fields)
// that have no shared-component equivalent
// =============================================================================

const fieldStyle: CSSProperties = {
  marginBottom: 16,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
  color: 'var(--streamlain-text-primary-color)',
};

const hintStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--streamlain-text-secondary-color)',
  marginBottom: 6,
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: 14,
  minHeight: 80,
  resize: 'vertical' as const,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box' as const,
};

const treeContainerStyle: CSSProperties = {
  maxHeight: 280,
  overflowY: 'auto',
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: 6,
  padding: 8,
  backgroundColor: 'var(--streamlain-background-primary-color)',
};

const treeNodeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 0',
  fontSize: 13,
  cursor: 'pointer',
  userSelect: 'none',
};

const chevronStyle = (expanded: boolean): CSSProperties => ({
  display: 'inline-block',
  width: 16,
  textAlign: 'center',
  fontSize: 10,
  color: 'var(--streamlain-text-secondary-color)',
  transform: expanded ? 'rotate(90deg)' : 'none',
  transition: 'transform 0.15s',
  flexShrink: 0,
});

const childrenIndentStyle: CSSProperties = {
  paddingLeft: 22,
};

const selectionSummaryStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--streamlain-text-secondary-color)',
  marginTop: 6,
};

// =============================================================================
// GraphQL
// =============================================================================

async function gqlFetch<T>(
  workspaceId: string,
  query: string,
  variables: Record<string, unknown>
): Promise<T> {
  const res = await requestWorkspaceSettingGraphQL(
    workspaceId,
    query,
    variables
  );
  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0].message);
  }
  return json.data;
}

const BOTS_QUERY = `
  query WorkspaceBots($workspaceId: String!) {
    workspaceBots(workspaceId: $workspaceId) {
      id name description systemPrompt accessDocIds intersectionMode isActive createdAt
    }
  }
`;

const CREATE_BOT_MUTATION = `
  mutation CreateBot($input: CreateBotInput!) {
    createBot(input: $input) {
      id name description systemPrompt accessDocIds intersectionMode isActive createdAt
    }
  }
`;

const UPDATE_BOT_MUTATION = `
  mutation UpdateBot($botId: String!, $input: UpdateBotInput!) {
    updateBot(botId: $botId, input: $input) {
      id name description systemPrompt accessDocIds intersectionMode isActive createdAt
    }
  }
`;

const DELETE_BOT_MUTATION = `
  mutation DeleteBot($botId: String!) {
    deleteBot(botId: $botId)
  }
`;

const FOLDERS_QUERY = `
  query MyAccessibleFolders($workspaceId: String!) {
    workspace(id: $workspaceId) {
      myAccessibleFolders {
        id name parentId isWorkspaceRoot docIds
      }
    }
  }
`;

const DOCS_QUERY = `
  query AccessibleDocs($workspaceId: String!) {
    workspace(id: $workspaceId) {
      docs(pagination: { first: 1000 }) {
        edges {
          node {
            id title trashed
          }
        }
      }
    }
  }
`;

// =============================================================================
// FolderDocPicker — Tree-based folder + doc selector (domain-specific, kept as-is)
// =============================================================================

interface TreeNode {
  id: string;
  name: string;
  type: 'folder' | 'doc';
  children: TreeNode[];
  docIds: string[]; // only for folders
}

function buildTree(
  folders: FolderInfo[],
  docs: DocInfo[],
  allDocIds: Set<string>
): TreeNode[] {
  const docMap = new Map(docs.map(d => [d.id, d]));
  const folderMap = new Map(folders.map(f => [f.id, f]));
  const childFolders = new Map<string | null, FolderInfo[]>();
  const docsInFolders = new Set<string>();

  for (const f of folders) {
    const parentKey = f.parentId;
    if (!childFolders.has(parentKey)) childFolders.set(parentKey, []);
    childFolders.get(parentKey)!.push(f);
    for (const did of f.docIds) docsInFolders.add(did);
  }

  function buildFolderNode(folder: FolderInfo): TreeNode {
    const children: TreeNode[] = [];
    // Sub-folders
    const subs = childFolders.get(folder.id) ?? [];
    for (const sub of subs.sort((a, b) => a.name.localeCompare(b.name))) {
      children.push(buildFolderNode(sub));
    }
    // Docs in this folder
    for (const docId of folder.docIds) {
      const doc = docMap.get(docId);
      if (doc) {
        children.push({
          id: docId,
          name: doc.title || 'Untitled',
          type: 'doc',
          children: [],
          docIds: [],
        });
      }
    }
    return {
      id: folder.id,
      name: folder.isWorkspaceRoot ? 'Workspace' : folder.name,
      type: 'folder',
      children,
      docIds: folder.docIds,
    };
  }

  // Find root folders (parentId is null or points to workspace root)
  const rootFolders = folders.filter(f => {
    if (f.isWorkspaceRoot) return true;
    if (f.parentId === null) return true;
    // Parent is workspace root — treated as top-level
    const parent = folderMap.get(f.parentId);
    return parent?.isWorkspaceRoot === true;
  });

  const wsRoot = rootFolders.find(f => f.isWorkspaceRoot);
  if (wsRoot) {
    // Use workspace root's children directly
    return buildFolderNode(wsRoot).children;
  }

  const tree: TreeNode[] = [];
  for (const f of rootFolders.sort((a, b) => a.name.localeCompare(b.name))) {
    tree.push(buildFolderNode(f));
  }

  // Orphan docs (not in any folder)
  for (const docId of allDocIds) {
    if (!docsInFolders.has(docId)) {
      const doc = docMap.get(docId);
      if (doc) {
        tree.push({
          id: docId,
          name: doc.title || 'Untitled',
          type: 'doc',
          children: [],
          docIds: [],
        });
      }
    }
  }

  return tree;
}

/** Collect all doc IDs reachable from a tree node (recursive). */
function collectDocIds(node: TreeNode): string[] {
  if (node.type === 'doc') return [node.id];
  const ids: string[] = [...node.docIds];
  for (const child of node.children) {
    ids.push(...collectDocIds(child));
  }
  return [...new Set(ids)];
}

function TreeNodeRow({
  node,
  selected,
  onToggle,
  depth,
}: {
  node: TreeNode;
  selected: Set<string>;
  onToggle: (docIds: string[], checked: boolean) => void;
  depth: number;
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children.length > 0;

  const allDocIds = useMemo(() => collectDocIds(node), [node]);
  const selectedCount = allDocIds.filter(id => selected.has(id)).length;
  const isChecked = allDocIds.length > 0 && selectedCount === allDocIds.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < allDocIds.length;

  const handleCheck = useCallback(() => {
    onToggle(allDocIds, !isChecked);
  }, [allDocIds, isChecked, onToggle]);

  return (
    <div>
      <div style={treeNodeStyle}>
        {hasChildren ? (
          <span
            style={chevronStyle(expanded)}
            onClick={() => setExpanded(!expanded)}
          >
            &#9654;
          </span>
        ) : (
          <span style={{ width: 16, flexShrink: 0 }} />
        )}
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={handleCheck}
        />
        <span
          onClick={() => (hasChildren ? setExpanded(!expanded) : handleCheck())}
          style={{
            color:
              node.type === 'folder'
                ? 'var(--streamlain-text-primary-color)'
                : 'var(--streamlain-text-secondary-color)',
            fontWeight: node.type === 'folder' ? 500 : 400,
          }}
        >
          {node.type === 'folder' ? '📁 ' : ''}
          {node.name}
          {node.type === 'folder' && (
            <span
              style={{
                fontSize: 11,
                color: 'var(--streamlain-text-disable-color)',
                marginLeft: 6,
              }}
            >
              ({allDocIds.length})
            </span>
          )}
        </span>
      </div>
      {expanded && hasChildren && (
        <div style={childrenIndentStyle}>
          {node.children.map(child => (
            <TreeNodeRow
              key={child.id}
              node={child}
              selected={selected}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderDocPicker({
  workspaceId,
  selected,
  onChange,
}: {
  workspaceId: string;
  selected: Set<string>;
  onChange: (docIds: Set<string>) => void;
}) {
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [docs, setDocs] = useState<DocInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [folderRes, docRes] = await Promise.all([
          gqlFetch<{
            workspace: {
              myAccessibleFolders: FolderInfo[];
            };
          }>(workspaceId, FOLDERS_QUERY, { workspaceId }),
          gqlFetch<{
            workspace: {
              docs: {
                edges: Array<{
                  node: { id: string; title: string; trashed: boolean };
                }>;
              };
            };
          }>(workspaceId, DOCS_QUERY, { workspaceId }),
        ]);
        if (!cancelled) {
          setFolders(folderRes.workspace.myAccessibleFolders);
          setDocs(
            docRes.workspace.docs.edges
              .map(e => e.node)
              .filter(d => !d.trashed)
              .map(d => ({ id: d.id, title: d.title }))
          );
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const allDocIds = useMemo(() => new Set(docs.map(d => d.id)), [docs]);

  const tree = useMemo(
    () => buildTree(folders, docs, allDocIds),
    [folders, docs, allDocIds]
  );

  const handleToggle = useCallback(
    (docIds: string[], checked: boolean) => {
      const next = new Set(selected);
      for (const id of docIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      onChange(next);
    },
    [selected, onChange]
  );

  const handleSelectAll = useCallback(() => {
    onChange(new Set(allDocIds));
  }, [allDocIds, onChange]);

  const handleDeselectAll = useCallback(() => {
    onChange(new Set());
  }, [onChange]);

  if (loading) {
    return (
      <div
        style={{
          fontSize: 13,
          color: 'var(--streamlain-text-secondary-color)',
          padding: 8,
        }}
      >
        Loading folders and documents...
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <button
          type="button"
          onClick={handleSelectAll}
          style={{
            fontSize: 12,
            color: 'var(--streamlain-primary-color)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Select all
        </button>
        <button
          type="button"
          onClick={handleDeselectAll}
          style={{
            fontSize: 12,
            color: 'var(--streamlain-text-secondary-color)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Deselect all
        </button>
      </div>
      <div style={treeContainerStyle}>
        {tree.length === 0 ? (
          <div
            style={{
              fontSize: 13,
              color: 'var(--streamlain-text-secondary-color)',
              textAlign: 'center',
              padding: 16,
            }}
          >
            No documents available.
          </div>
        ) : (
          tree.map(node => (
            <TreeNodeRow
              key={node.id}
              node={node}
              selected={selected}
              onToggle={handleToggle}
              depth={0}
            />
          ))
        )}
      </div>
      <div style={selectionSummaryStyle}>
        {selected.size === 0
          ? 'No selection — agent has access to all documents.'
          : `${selected.size} document${selected.size !== 1 ? 's' : ''} selected.`}
      </div>
    </div>
  );
}

// =============================================================================
// BotFormDialog — Create / Edit via SettingsEntityFormDialog
// =============================================================================

const EMPTY_FORM: BotFormData = {
  name: '',
  description: '',
  systemPrompt: '',
  accessDocIds: [],
  intersectionMode: true,
};

function BotFormDialog({
  open,
  onClose,
  initial,
  onSubmit,
  title,
  submitLabel,
  workspaceId,
}: {
  open: boolean;
  onClose: () => void;
  initial: BotFormData;
  onSubmit: (data: BotFormData) => void;
  title: string;
  submitLabel: string;
  workspaceId: string;
}) {
  const [form, setForm] = useState<BotFormData>(initial);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(
    () => new Set(initial.accessDocIds)
  );

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      setForm(initial);
      setSelectedDocs(new Set(initial.accessDocIds));
    }
  }, [open, initial]);

  const handleSubmit = useCallback(() => {
    if (!form.name.trim()) return;
    onSubmit({
      ...form,
      accessDocIds: [...selectedDocs],
    });
  }, [form, selectedDocs, onSubmit]);

  return (
    <SettingsEntityFormDialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
      width={560}
      title={title}
      onSave={handleSubmit}
      onCancel={onClose}
      saveLabel={submitLabel}
      cancelLabel="Cancel"
      saveDisabled={!form.name.trim()}
      saveTestId="bot-form-submit"
      contentTestId="bot-form-content"
    >
      {/* Name */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Name</label>
        <input
          data-testid="bot-form-name"
          value={form.name}
          onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. Accounting Expert"
          style={{
            ...textareaStyle,
            minHeight: 'auto',
            height: 36,
            resize: 'none',
          }}
        />
      </div>

      {/* Description */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Description</label>
        <div style={hintStyle}>Short description shown to other users.</div>
        <input
          data-testid="bot-form-description"
          value={form.description}
          onChange={e =>
            setForm(prev => ({ ...prev, description: e.target.value }))
          }
          placeholder="e.g. Answers accounting questions"
          style={{
            ...textareaStyle,
            minHeight: 'auto',
            height: 36,
            resize: 'none',
          }}
        />
      </div>

      {/* Instructions (was: System Prompt) */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Instructions</label>
        <div style={hintStyle}>
          Describe how the agent should behave. Example: &quot;Always respond in
          English and focus on accounting topics.&quot;
        </div>
        <textarea
          data-testid="bot-form-system-prompt"
          style={textareaStyle}
          value={form.systemPrompt}
          onChange={e =>
            setForm(prev => ({ ...prev, systemPrompt: e.target.value }))
          }
          placeholder="Describe the agent's behavior..."
        />
      </div>

      {/* Folder / Doc Picker */}
      <div style={fieldStyle}>
        <label style={labelStyle}>Knowledge Base</label>
        <div style={hintStyle}>
          Select folders and documents the agent can access. Without a
          selection, the agent has access to all workspace documents.
        </div>
        <FolderDocPicker
          workspaceId={workspaceId}
          selected={selectedDocs}
          onChange={setSelectedDocs}
        />
      </div>
    </SettingsEntityFormDialog>
  );
}

// =============================================================================
// BotCard — expandable card via SettingsExpandableItem
// =============================================================================

function BotCard({
  bot,
  onEdit,
  onDelete,
  canEdit,
}: {
  bot: WorkspaceBot;
  onEdit: (bot: WorkspaceBot) => void;
  onDelete: (botId: string) => void;
  canEdit: boolean;
}) {
  const t = useI18n();
  const [expanded, setExpanded] = useState(false);

  const meta =
    bot.accessDocIds.length > 0 ? (
      <SettingsMetaItem>
        {bot.accessDocIds.length} document
        {bot.accessDocIds.length !== 1 ? 's' : ''} assigned
      </SettingsMetaItem>
    ) : undefined;

  return (
    <SettingsExpandableItem
      title={<span data-testid={`bot-name-${bot.id}`}>{bot.name}</span>}
      meta={meta}
      expanded={expanded}
      onToggle={() => setExpanded(v => !v)}
      dataTestId={`bot-card-${bot.id}`}
    >
      {bot.description ? (
        <SettingsContentSection>
          <SettingsContentText>{bot.description}</SettingsContentText>
        </SettingsContentSection>
      ) : null}

      {canEdit && (
        <SettingsActionsSection title="Actions">
          <Button
            data-testid={`bot-edit-${bot.id}`}
            variant="secondary"
            onClick={() => onEdit(bot)}
          >
            Edit
          </Button>
          <SettingsConfirmButton
            label="Delete"
            confirmLabel={
              t['com.streamlain.settings.workspace.bots.delete-confirm']?.() ??
              'Delete bestaetigen'
            }
            cancelLabel="Cancel"
            onConfirm={() => onDelete(bot.id)}
            variant="error"
            dataTestId={`bot-delete-${bot.id}`}
          />
        </SettingsActionsSection>
      )}
    </SettingsExpandableItem>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export const WorkspaceSettingBots = ({
  personalMemoryEnabled,
  knowledgeGraphEnabled,
  bare,
}: {
  personalMemoryEnabled: boolean;
  knowledgeGraphEnabled: boolean;
  /** When true, renders content without SettingWrapper (for embedding in another section) */
  bare?: boolean;
}) => {
  const t = useI18n();
  const { isFeatureAllowed, getRequiredPlan, notifyFeatureLocked } =
    useFeatureGate();
  const workspacePermissionService = useService(
    WorkspacePermissionService
  ).permission;
  const isOwner = useLiveData(workspacePermissionService.isOwner$);
  const isAdmin = useLiveData(workspacePermissionService.isAdmin$);
  const canEdit = Boolean(isOwner || isAdmin);
  const workspaceId = useService(WorkspaceService).workspace.id;

  const embeddingService = useService(EmbeddingService);
  const docEmbeddingEnabled = useLiveData(
    embeddingService.embeddingEnabled.enabled$
  );

  const botsAllowed = isFeatureAllowed('ai-expert-bots');
  const requiredPlan = getRequiredPlan('ai-expert-bots');

  useEffect(() => {
    embeddingService.embeddingEnabled.getEnabled();
  }, [embeddingService.embeddingEnabled]);

  const prerequisitesMet =
    docEmbeddingEnabled && personalMemoryEnabled && knowledgeGraphEnabled;

  const fetcher = useCallback(
    () =>
      gqlFetch<{ workspaceBots: WorkspaceBot[] }>(workspaceId, BOTS_QUERY, {
        workspaceId,
      }).then(d => d.workspaceBots),
    [workspaceId]
  );

  const {
    data: bots,
    isLoading,
    refresh: fetchBots,
  } = useSettingsEntityList<WorkspaceBot>(fetcher, [workspaceId]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBot, setEditingBot] = useState<WorkspaceBot | null>(null);

  const handleOpenCreate = useCallback(() => {
    if (!botsAllowed) {
      notifyFeatureLocked('ai-expert-bots');
      return;
    }
    setEditingBot(null);
    setDialogOpen(true);
  }, [botsAllowed, notifyFeatureLocked]);

  const handleOpenEdit = useCallback((bot: WorkspaceBot) => {
    setEditingBot(bot);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingBot(null);
  }, []);

  const handleDialogSubmit = useCallback(
    (data: BotFormData) => {
      if (!botsAllowed || !prerequisitesMet) return;
      void (async () => {
        try {
          if (editingBot) {
            await gqlFetch(workspaceId, UPDATE_BOT_MUTATION, {
              botId: editingBot.id,
              input: {
                name: data.name,
                description: data.description,
                systemPrompt: data.systemPrompt,
                accessDocIds: data.accessDocIds,
                intersectionMode: true,
              },
            });
          } else {
            await gqlFetch(workspaceId, CREATE_BOT_MUTATION, {
              input: {
                workspaceId,
                name: data.name,
                description: data.description,
                systemPrompt: data.systemPrompt,
                accessDocIds: data.accessDocIds,
                intersectionMode: true,
              },
            });
          }
          setDialogOpen(false);
          setEditingBot(null);
          await fetchBots();
        } catch {
          // Error handling
        }
      })();
    },
    [workspaceId, botsAllowed, prerequisitesMet, editingBot, fetchBots]
  );

  const handleDelete = useCallback(
    (botId: string) => {
      void (async () => {
        try {
          await gqlFetch(workspaceId, DELETE_BOT_MUTATION, { botId });
          await fetchBots();
        } catch {
          // Error handling
        }
      })();
    },
    [fetchBots, workspaceId]
  );

  const dialogInitial: BotFormData = editingBot
    ? {
        name: editingBot.name,
        description: editingBot.description,
        systemPrompt: editingBot.systemPrompt,
        accessDocIds: editingBot.accessDocIds,
        intersectionMode: true,
      }
    : EMPTY_FORM;

  const content = (
    <>
      {!botsAllowed && (
        <SettingsNotice title="Plan required">
          Expert Agents requires the {requiredPlan ?? 'Team'} plan.
        </SettingsNotice>
      )}
      {botsAllowed && !prerequisitesMet && (
        <SettingsNotice title="Prerequisites missing">
          Expert Agents require both <strong>Personal AI Memory</strong> and{' '}
          <strong>Knowledge Graph</strong> to be enabled.
          {!docEmbeddingEnabled &&
            ' Additionally, Document Analysis must be turned on.'}{' '}
          Please adjust the settings above.
        </SettingsNotice>
      )}

      <div
        style={
          !prerequisitesMet || !botsAllowed
            ? { opacity: 0.4, pointerEvents: 'none' as const }
            : undefined
        }
      >
        {isLoading && (
          <div
            style={{
              padding: '12px 0',
              color: 'var(--streamlain-text-secondary-color)',
            }}
          >
            Loading agents...
          </div>
        )}

        {!isLoading && (bots ?? []).length === 0 && (
          <div
            data-testid="bot-empty-state"
            style={{
              padding: '24px 0',
              textAlign: 'center',
              color: 'var(--streamlain-text-secondary-color)',
            }}
          >
            {t['com.streamlain.settings.workspace.bots.empty']?.() ??
              'No agents yet. Create one to get started.'}
          </div>
        )}

        {(bots ?? []).length > 0 && (
          <SettingsEntityList>
            {(bots ?? []).map(bot => (
              <BotCard
                key={bot.id}
                bot={bot}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                canEdit={canEdit}
              />
            ))}
          </SettingsEntityList>
        )}

        {canEdit && (
          <div style={{ textAlign: 'center', paddingTop: 4 }}>
            <Button
              data-testid="bot-create-button"
              variant="primary"
              onClick={handleOpenCreate}
            >
              {t['com.streamlain.settings.workspace.bots.create']?.() ??
                'Create Agent'}
            </Button>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <BotFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        initial={dialogInitial}
        onSubmit={handleDialogSubmit}
        title={editingBot ? 'Edit Agent' : 'Create New Agent'}
        submitLabel={editingBot ? 'Save' : 'Create Agent'}
        workspaceId={workspaceId}
      />
    </>
  );

  if (bare) return content;

  return (
    <SettingWrapper
      title={
        t['com.streamlain.settings.workspace.bots.title']?.() ?? 'Expert Agents'
      }
      testId="workspace-setting-bots"
    >
      {content}
    </SettingWrapper>
  );
};
